/**
 * 起動シーン。
 *  ・仮アイコンのテクスチャを生成する
 *  ・存在が確認できた実画像だけを読み込む（404 を踏まないため）
 *  ・読み込みに失敗した画像は仮アイコンへフォールバックする
 */

import Phaser from 'phaser';

import { SPARK_TEXTURE_KEY } from '../effects/MergeEffects';
import type { GameContext } from '../GameContext';
import { GameState } from '../../core/GameState';
import { imageTextureKey, placeholderTextureKey, sceneryPhotoKey } from '../utils/assetLoader';
import { drawItemIcon, iconRenderSize } from '../utils/iconFactory';
import { createGlowTexture, createSteamTexture } from '../utils/sceneryTextures';
import { BACKGROUND_SCENE_KEY } from './BackgroundScene';
import { GAME_SCENE_KEY } from './GameScene';

export const BOOT_SCENE_KEY = 'BootScene';

export class BootScene extends Phaser.Scene {
  private context!: GameContext;

  constructor() {
    super({ key: BOOT_SCENE_KEY });
  }

  preload(): void {
    this.context = this.registry.get('context') as GameContext;

    // 進み具合は盤面の上に重ねた HTML の幕（StageLoader）が見せる
    this.load.on(Phaser.Loader.Events.PROGRESS, (value: number) => {
      this.context.reportLoadProgress?.(value);
    });
    this.createSparkTexture();
    createGlowTexture(this);
    createSteamTexture(this);
    this.createPlaceholderTextures();

    // 実画像は「存在が確認できたもの」だけ読み込む
    this.context.items.forEach((item) => {
      if (!this.context.icons.hasRealImage(item)) return;
      this.load.image(imageTextureKey(item), item.image!);
    });

    // 背景写真も同様（1枚も無ければ手続き生成の夜景のまま）
    this.context.sceneryPhotos.forEach((url, phase) => {
      this.load.image(sceneryPhotoKey(phase), url);
    });

    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      const item = this.context.items.find((entry) => imageTextureKey(entry) === file.key);
      if (item) {
        console.warn(
          `[BootScene] 画像を読み込めませんでした: ${item.image} → 仮アイコンを使用します`,
        );
        this.context.icons.markUnavailable(item.id);
        return;
      }
      if (file.key.startsWith('scenery-photo:')) {
        console.warn(`[BootScene] 背景写真を読み込めませんでした: ${file.key} → 情景描画を使用`);
      }
    });
  }

  create(): void {
    this.context.state.transition(GameState.READY);
    // 背景（情景）を先に起動し、その上でゲーム本体を動かす
    this.scene.launch(BACKGROUND_SCENE_KEY);
    this.scene.start(GAME_SCENE_KEY);
  }

  /** 粒子演出用の小さな光テクスチャ。 */
  private createSparkTexture(): void {
    if (this.textures.exists(SPARK_TEXTURE_KEY)) return;
    const size = 24;
    const texture = this.textures.createCanvas(SPARK_TEXTURE_KEY, size, size);
    if (!texture) return;
    const ctx = texture.context;
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.4, 'rgba(255,255,255,0.7)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    texture.refresh();
  }

  /** 全アイテムの仮アイコンテクスチャを生成する。 */
  private createPlaceholderTextures(): void {
    this.context.items.forEach((item) => {
      const key = placeholderTextureKey(item);
      if (this.textures.exists(key)) return;
      const size = iconRenderSize(item.radius);
      const texture = this.textures.createCanvas(key, size, size);
      if (!texture) return;
      drawItemIcon(texture.context, item, size);
      texture.refresh();
    });
  }
}
